import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";

import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { MessageService } from "@openng/optimus-ui/api";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { SelectModule } from "@openng/optimus-ui/select";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { COUNTRY_OPTIONS } from "../../../../../constants/countryOptions";
import { CacheService } from "../../../../../services/cache/cache.service";
import { environment } from "../../../../../../environments/environment";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { Customer, CustomerDTO } from "../../../../../models/api/asset/customer";
import { first } from "rxjs";
import { EMAIL_PATTERN } from "../../../../../constants/regExp";
import { PermissionService } from "../../../../../services/api/permission/permission.service";

@Component({
  standalone: true,
  selector: "app-customer",
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    SelectModule,
  ],
  templateUrl: "./customer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./customer.component.scss",
})
export class CustomerComponent implements OnInit {
  public countries: DropdownOption[] = COUNTRY_OPTIONS;
  public requestStarted = signal(false);
  private config = inject(DynamicDialogConfig);
  public customer: Customer = this.config.data.asset;
  private permissions = inject(PermissionService);
  public form = new FormGroup({
    label: new FormControl(
      {
        value: this.customer.label,
        disabled: !this.permissions.hasPermission([
          this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE,
          this.permissions.allPermissions.ASSET_MASTER_DATA_UPDATE,
        ]),
      },
      Validators.required
    ),
    email: new FormControl(this.customer.email, [Validators.pattern(EMAIL_PATTERN)]),
    phone: new FormControl(this.customer.phone),

    mobilePhone: new FormControl(this.customer.mobilePhone),
    notes: new FormControl(this.customer.notes),
    address: new FormGroup({
      street: new FormControl(this.customer.address?.street),
      number: new FormControl(this.customer.address?.number),
      zipCode: new FormControl(this.customer.address?.zipCode),
      city: new FormControl(this.customer.address?.city),
      country: new FormControl(this.customer.address?.country),
    }),
  });
  private ref = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  private message = inject(MessageService);
  private assetService = inject(AssetService);
  private actionType!: "add" | "edit";
  private cache = inject(CacheService);

  public ngOnInit(): void {
    if (this.config.data.actionType) {
      this.actionType = this.config.data.actionType;
    }
  }

  /**
   * Submits the form if valid and no request is already in progress. If actionType is "add", creates a new customer asset. If actionType is "edit", updates the customer asset.
   * Shows an error message if the form is not valid.
   */
  public save(): void {
    const customerDTO = this.getCustomerDTO();

    if (customerDTO && this.form?.valid) {
      if (!this.requestStarted()) {
        this.customer.properties = customerDTO;
        this.requestStarted.set(true);
        if (this.actionType === "add") {
          this.assetService
            .createAsset(ASSET_TYPES.CUSTOMER, this.customer, this.customer.directParentId)
            .pipe(first())
            .subscribe(this.afterServiceCommunication());
        } else if (this.actionType === "edit") {
          this.assetService.editAsset(ASSET_TYPES.CUSTOMER, this.customer).subscribe(this.afterServiceCommunication());
        }
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  /**
   * Closes the dialog without returning any value.
   */
  closeDialog() {
    this.ref.close();
  }

  /**
   * Gets the CustomerDTO object from the form values.
   * Returns null if the form is invalid or the customer label is not provided.
   * @returns CustomerDTO
   */
  private getCustomerDTO(): CustomerDTO | null {
    const customerFormValues = this.form?.getRawValue();
    if (customerFormValues && customerFormValues.label) {
      return {
        ...customerFormValues,
        label: customerFormValues.label,
      };
    } else {
      return null;
    }
  }

  /**
   * Handles the server communication response for customer asset operations.
   * - `next`: Executes on successful response, clears request state, and closes the dialog.
   *   If the action type is "edit", it also deletes the edited customer from the cache.
   * - `error`: Executes on error response, clears request state, and displays an error message.
   */
  private afterServiceCommunication() {
    return {
      next: () => {
        if (this.actionType === "edit") {
          this.cache.delete(environment.baseUrl + `/asset/${this.customer.id}?assetType=CUSTOMER`);
        }
        this.requestStarted.set(false);
        this.ref.close(true);
      },
      error: () => {
        this.requestStarted.set(false);
      },
    };
  }
}
